import Axios from 'middleware/axiosInstance'
import React, { useEffect, useState } from 'react'
import { Button, Space, Table, message, Card, Typography } from "antd";
import { useNavigate } from 'react-router-dom';
import Loading from 'layouts/Loading/Loading';
import './FoodSorting.scss';

const { Title, Text } = Typography;

const FoodSorting = () => {
    const navigate = useNavigate();

    const [allFoods, setAllFoods] = useState([]);
    const [hasOrderChanged, setHasOrderChanged] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const getAllFoods = () => {
        Axios.get("/food")
            .then((res) => {
                setAllFoods(res.data || []);
            })
            .catch(err => {
                console.log(err);
                message.error('خطا در بارگذاری لیست غذاها');
            });
    };

    useEffect(() => {
        getAllFoods();
    }, []);

    // Group foods by category
    const groupedFoods = allFoods.reduce((groups, food) => {
        const categoryId = food.category?.id || 'no-category';
        const categoryName = food.category?.title_fa || 'بدون دسته‌بندی';
        
        if (!groups[categoryId]) {
            groups[categoryId] = {
                categoryId,
                categoryName,
                foods: []
            };
        }
        groups[categoryId].foods.push(food);
        return groups;
    }, {});

    // Convert grouped foods to array for display
    const groupedFoodsArray = Object.values(groupedFoods);

    // Handle drag and drop within a specific category
    const handleDragStart = (e, categoryId, foodIndex) => {
        e.dataTransfer.setData('text/plain', JSON.stringify({ categoryId, foodIndex }));
        e.target.style.opacity = '0.5';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e, dropCategoryId, dropFoodIndex) => {
        e.preventDefault();
        const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
        const { categoryId: dragCategoryId, foodIndex: dragFoodIndex } = dragData;
        
        // Only allow reordering within the same category
        if (dragCategoryId === dropCategoryId && dragFoodIndex !== dropFoodIndex) {
            const newFoods = [...allFoods];
            const categoryFoods = newFoods.filter(food => food.category?.id === dragCategoryId);
            
            // Reorder within the category
            const [draggedItem] = categoryFoods.splice(dragFoodIndex, 1);
            categoryFoods.splice(dropFoodIndex, 0, draggedItem);
            
            // Update the main foods array with new order
            let foodIndex = 0;
            newFoods.forEach((food, index) => {
                if (food.category?.id === dragCategoryId) {
                    newFoods[index] = categoryFoods[foodIndex];
                    foodIndex++;
                }
            });
            
            setAllFoods(newFoods);
            setHasOrderChanged(true);
        }
        
        e.target.style.opacity = '1';
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = '1';
    };

    // Submit new order to backend
    const submitNewOrder = async () => {
        if (!hasOrderChanged) {
            message.info('ترتیب تغییر نکرده است');
            return;
        }

        setIsSubmitting(true);
        try {
            const orderData = [];
            
            // Create order data for each category separately
            groupedFoodsArray.forEach(categoryGroup => {
                categoryGroup.foods.forEach((food, index) => {
                    orderData.push({
                        id: food.id,
                        categoryId: categoryGroup.categoryId,
                        order: index + 1
                    });
                });
            });

            const response = await Axios.post('/food/reorder', orderData);
            
            if (response.status === 200) {
                message.success('ترتیب با موفقیت ذخیره شد');
                setHasOrderChanged(false);
            }
        } catch (error) {
            console.error('Error updating order:', error);
            message.error('خطا در ذخیره ترتیب');
        } finally {
            setIsSubmitting(false);
        }
    };

    const columns = [
        {
            title: "ترتیب",
            dataIndex: "order",
            key: "order",
            width: 80,
            render: (_, record, index) => (
                <div className="order-number">
                    {index + 1}
                </div>
            ),
        },
        {
            title: "نام غذا",
            dataIndex: "name_fa",
            key: "name_fa",
            sorter: (a, b) =>
                a.name_fa > b.name_fa ? 1 : b.name_fa > a.name_fa ? -1 : 0,
            ellipsis: true,
        },
        {
            title: "دسته بندی",
            dataIndex: "category",
            key: "category",
            render: (_, item) => (
                <span>{item.category?.title_fa}</span>
            ),
        },
    ];

    return (
        <div className="customized-container food-sorting-page">
            <div className="customized-wrapper">
                <Card>
                    <div className="page-header">
                        <Title level={3}>مرتب‌سازی غذاها بر اساس دسته‌بندی</Title>
                        <Text type="secondary">
                            برای تغییر ترتیب غذاها، روی هر ردیف کلیک کرده و آن را به موقعیت جدید بکشید. 
                            توجه: فقط می‌توانید غذاها را درون دسته‌بندی خودشان جابجا کنید.
                        </Text>
                    </div>

                    <div className="action-buttons">
                        <Space>
                            <Button 
                                type="primary" 
                                onClick={submitNewOrder}
                                loading={isSubmitting}
                                disabled={!hasOrderChanged}
                            >
                                ذخیره ترتیب جدید
                            </Button>
                            <Button 
                                onClick={() => {
                                    setHasOrderChanged(false);
                                    getAllFoods();
                                }}
                            >
                                بازنشانی
                            </Button>
                            <Button 
                                type="success" 
                                onClick={() => navigate('/foods')}
                            >
                                بازگشت به لیست غذاها
                            </Button>
                        </Space>
                    </div>

                    {/* Info */}
                    {hasOrderChanged && (
                        <div className="info-alert">
                            <Text type="success">
                                ⚠️ ترتیب تغییر کرده است. برای ذخیره تغییرات روی "ذخیره ترتیب جدید" کلیک کنید.
                            </Text>
                        </div>
                    )}

                    {allFoods.length > 0 ? (
                        <div className="sorting-tables">
                            {groupedFoodsArray.map((categoryGroup, categoryIndex) => (
                                <div key={categoryGroup.categoryId} className="category-section">
                                    <div className="category-header">
                                        <Title level={4}>{categoryGroup.categoryName}</Title>
                                        <Text type="secondary">
                                            {categoryGroup.foods.length} غذا
                                        </Text>
                                    </div>
                                    
                                    <div className="sorting-table">
                                        <Table
                                            pagination={false}
                                            columns={columns}
                                            dataSource={categoryGroup.foods}
                                            rowKey="id"
                                            onRow={(record, index) => ({
                                                draggable: true,
                                                onDragStart: (e) => handleDragStart(e, categoryGroup.categoryId, index),
                                                onDragOver: (e) => handleDragOver(e),
                                                onDrop: (e) => handleDrop(e, categoryGroup.categoryId, index),
                                                onDragEnd: handleDragEnd,
                                                style: { cursor: 'grab' },
                                                className: 'draggable-row',
                                            })}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="loading-container">
                            <Loading />
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default FoodSorting;
